import { apiClient, handleApiResponse } from './client';
import { ApiResponse, UploadSession, UploadProgress, FileUpload } from '../types';

export const uploadApi = {
  /**
   * Initiate a file upload session
   */
  initiateUpload: async (filename: string, size: number, mimeType: string): Promise<ApiResponse<UploadSession>> => {
    const payload = {
      filename,
      size,
      mimeType,
    };
    console.log('Upload API - Initiate payload:', payload);
    console.log('Upload API - Parameters:', { filename, size, mimeType });
    
    const response = await apiClient.post<ApiResponse<UploadSession>>('/upload/initiate', payload);
    return handleApiResponse(response);
  },

  /**
   * Upload file
   */
  uploadFile: async (
    uploadId: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<FileUpload>> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('uploadId', uploadId);

    // Calculate timeout based on file size (minimum 10 minutes, add 1 minute per 100MB)
    const fileSizeInMB = file.size / (1024 * 1024);
    const timeoutMinutes = Math.max(10, Math.ceil(fileSizeInMB / 100) + 10);
    const timeoutMs = timeoutMinutes * 60 * 1000;

    console.log(`Upload timeout set to ${timeoutMinutes} minutes for ${fileSizeInMB.toFixed(2)}MB file`);

    const response = await apiClient.post<ApiResponse<FileUpload>>('/upload/file', formData, {
      timeout: timeoutMs, // Dynamic timeout based on file size
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const percentage = (progressEvent.loaded / progressEvent.total) * 100;
          onProgress(percentage);
        }
      },
    });

    return handleApiResponse(response);
  },

  /**
   * Get upload progress
   */
  getUploadProgress: async (uploadId: string): Promise<ApiResponse<UploadProgress>> => {
    const response = await apiClient.get<ApiResponse<UploadProgress>>(`/upload/progress/${uploadId}`);
    return handleApiResponse(response);
  },

  /**
   * Cancel upload
   */
  cancelUpload: async (uploadId: string): Promise<ApiResponse> => {
    const response = await apiClient.delete<ApiResponse>(`/upload/${uploadId}`);
    return handleApiResponse(response);
  },

  /**
   * Resume upload (for future implementation)
   */
  resumeUpload: async (uploadId: string): Promise<ApiResponse<UploadProgress>> => {
    const response = await apiClient.post<ApiResponse<UploadProgress>>(`/upload/resume/${uploadId}`);
    return handleApiResponse(response);
  },

  /**
   * Stage file (upload to Azure but don't save to database)
   */
  stageFile: async (
    uploadId: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<{ azureFileName: string; uploadId: string; isCompressed?: boolean; wasOriginallyZip?: boolean; compressionRatio?: number; spaceSaved?: string; }>> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('uploadId', uploadId);

    // Calculate timeout based on file size
    const fileSizeInMB = file.size / (1024 * 1024);
    const timeoutMinutes = Math.max(10, Math.ceil(fileSizeInMB / 100) + 10);
    const timeoutMs = timeoutMinutes * 60 * 1000;

    const response = await apiClient.post(`/upload/stage`, formData, {
      timeout: timeoutMs,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const percentage = (progressEvent.loaded / progressEvent.total) * 100;
          onProgress(percentage);
        }
      },
    });

    return handleApiResponse(response);
  },

  /**
   * Commit staged file to database
   */
  commitStagedFile: async (uploadId: string, azureFileName: string): Promise<ApiResponse<FileUpload>> => {
    const response = await apiClient.post<ApiResponse<FileUpload>>('/upload/commit', {
      uploadId,
      azureFileName,
    });
    return handleApiResponse(response);
  },

  /**
   * Clean up staged file (delete from Azure without saving to database)
   */
  cleanupStagedFile: async (azureFileName: string): Promise<ApiResponse> => {
    const response = await apiClient.delete<ApiResponse>(`/upload/staged/${encodeURIComponent(azureFileName)}`);
    return handleApiResponse(response);
  },

  /**
   * Get SAS URL for direct blob upload (bypasses App Service timeout)
   */
  getSASUpload: async (filename: string, size: number, mimeType: string, durationMinutes: number = 30): Promise<ApiResponse<{
    uploadId: string;
    blobName: string;
    sasUrl: string;
    expiresAt: string;
    containerName: string;
    filename: string;
    maxFileSize: number;
  }>> => {
    const payload = {
      filename,
      size,
      mimeType,
      durationMinutes,
    };
    
    const response = await apiClient.post<ApiResponse<any>>('/upload/get-sas', payload);
    return handleApiResponse(response);
  },

  /**
   * Upload file through backend proxy (solves VNet issues)
   */
  uploadToBlobDirect: async (
    _sasUrl: string, // Unused parameter - kept for API compatibility
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<boolean> => {
    try {
      const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunks for better reliability
      
      // Always use chunked upload through backend for better reliability and VNet compatibility
      console.log('Using backend proxy chunked upload for file size:', file.size, 'bytes');
      return uploadApi.uploadChunkedThroughBackend(file, CHUNK_SIZE, onProgress);
      
    } catch (error) {
      console.error('Backend proxy upload failed:', error);
      throw error;
    }
  },

  /**
   * Chunked upload through backend proxy for all file sizes
   */
  uploadChunkedThroughBackend: async (
    file: File,
    chunkSize: number = 10 * 1024 * 1024, // 10MB default
    onProgress?: (progress: number) => void
  ): Promise<boolean> => {
    try {
      const totalChunks = Math.ceil(file.size / chunkSize);
      let uploadedBytes = 0;

      console.log(`Starting backend proxy chunked upload: ${totalChunks} chunks of ${chunkSize} bytes each`);

      // Upload each chunk through backend
      for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);
        
        // Create a new File object for the chunk
        const chunkFile = new File([chunk], `chunk_${i}_${file.name}`, { type: file.type });
        
        // Upload chunk through backend
        await uploadApi.uploadChunkThroughBackend(chunkFile, i, totalChunks, file.name);
        
        uploadedBytes += chunk.size;
        
        // Update progress
        if (onProgress) {
          const percentage = (uploadedBytes / file.size) * 100;
          onProgress(percentage);
        }
        
        console.log(`Uploaded chunk ${i + 1}/${totalChunks} (${end - start} bytes) through backend`);
      }

      // Notify backend that all chunks are uploaded
      await uploadApi.completeChunkedUpload(file.name, totalChunks);
      
      console.log('Backend proxy chunked upload completed successfully');
      return true;
      
    } catch (error) {
      console.error('Backend proxy chunked upload failed:', error);
      throw error;
    }
  },

  /**
   * Upload a single chunk through backend
   */
  uploadChunkThroughBackend: async (
    chunkFile: File,
    chunkIndex: number,
    totalChunks: number,
    originalFileName: string
  ): Promise<void> => {
    const formData = new FormData();
    formData.append('file', chunkFile);
    formData.append('chunkIndex', chunkIndex.toString());
    formData.append('totalChunks', totalChunks.toString());
    formData.append('fileName', originalFileName);

    const response = await apiClient.post<ApiResponse<any>>('/upload/chunk', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'X-Upload-Total-Chunks': totalChunks.toString(),
      },
      timeout: 300000, // 5 minutes timeout for large chunks
    });

    if (!response.data.success) {
      throw new Error(`Chunk upload failed: ${response.data.error?.message || 'Unknown error'}`);
    }
  },

  /**
   * Complete chunked upload and trigger backend processing
   */
  completeChunkedUpload: async (
    fileName: string,
    totalChunks: number
  ): Promise<void> => {
    const payload = {
      fileName,
      totalChunks,
      action: 'complete'
    };
    // Completion triggers server-side combine/validation/upload, which can exceed default 30s.
    // Timeout scales with chunk count and retries once on timeout.
    const estimatedSeconds = Math.max(180, Math.min(1800, 60 + (totalChunks * 20))); // 3m..30m
    const firstTimeoutMs = estimatedSeconds * 1000;
    const retryTimeoutMs = Math.min(firstTimeoutMs * 2, 45 * 60 * 1000); // cap retry at 45m
    let response;

    try {
      response = await apiClient.post<ApiResponse<any>>('/upload/chunk', payload, {
        timeout: firstTimeoutMs,
        headers: {
          'X-Upload-Total-Chunks': totalChunks.toString(),
        },
      });
    } catch (error: any) {
      const isTimeout = error?.code === 'ECONNABORTED' || `${error?.message || ''}`.includes('timeout');
      if (!isTimeout) {
        throw error;
      }

      console.warn(
        `Chunked upload completion timed out after ${Math.round(firstTimeoutMs / 1000)}s. Retrying once with longer timeout...`
      );

      response = await apiClient.post<ApiResponse<any>>('/upload/chunk', payload, {
        timeout: retryTimeoutMs,
        headers: {
          'X-Upload-Total-Chunks': totalChunks.toString(),
        },
      });
    }
    
    if (!response.data.success) {
      throw new Error(`Chunked upload completion failed: ${response.data.error?.message || 'Unknown error'}`);
    }
  },

  /**
   * Simple PUT upload for smaller files
   */
  uploadSimplePUT: async (
    sasUrl: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<boolean> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
 
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const percentage = (event.loaded / event.total) * 100;
          onProgress(percentage);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(true);
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload was aborted'));
      });

      // Open PUT request to SAS URL
      xhr.open('PUT', sasUrl);
      
      // Set required headers for blob upload
      xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      
      // Start the upload
      xhr.send(file);
    });
  },

  /**
   * Chunked upload for large files using Azure Block Blob API
   */
  uploadChunked: async (
    sasUrl: string,
    file: File,
    chunkSize: number,
    onProgress?: (progress: number) => void
  ): Promise<boolean> => {
    try {
      const totalChunks = Math.ceil(file.size / chunkSize);
      const blockIds: string[] = [];
      let uploadedBytes = 0;

      console.log(`Starting chunked upload: ${totalChunks} chunks of ${chunkSize} bytes each`);

      // Upload each chunk
      for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);
        
        // Generate block ID (must be base64 encoded and same length for all blocks)
        const blockId = btoa(`block-${i.toString().padStart(6, '0')}`);
        blockIds.push(blockId);
        
        // Upload chunk
        await uploadApi.uploadChunk(sasUrl, chunk, blockId);
        
        uploadedBytes += chunk.size;
        
        // Update progress
        if (onProgress) {
          const percentage = (uploadedBytes / file.size) * 100;
          onProgress(percentage);
        }
        
        console.log(`Uploaded chunk ${i + 1}/${totalChunks} (${end - start} bytes)`);
      }

      // Commit all blocks
      console.log('Committing blocks:', blockIds.length);
      await uploadApi.commitBlocks(sasUrl, blockIds);
      
      console.log('Chunked upload completed successfully');
      return true;
      
    } catch (error) {
      console.error('Chunked upload failed:', error);
      throw error;
    }
  },

  /**
   * Upload a single chunk (block)
   */
  uploadChunk: async (sasUrl: string, chunk: Blob, blockId: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Chunk upload failed with status ${xhr.status}: ${xhr.statusText}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during chunk upload'));
      });

      // PUT Block URL
      const blockUrl = `${sasUrl}&comp=block&blockid=${encodeURIComponent(blockId)}`;
      xhr.open('PUT', blockUrl);
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      
      xhr.send(chunk);
    });
  },

  /**
   * Commit all uploaded blocks
   */
  commitBlocks: async (sasUrl: string, blockIds: string[]): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Block commit failed with status ${xhr.status}: ${xhr.statusText}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during block commit'));
      });

      // Create XML body for commit
      const blockListXml = `<?xml version="1.0" encoding="utf-8"?>
<BlockList>
${blockIds.map(id => `  <Latest>${id}</Latest>`).join('\n')}
</BlockList>`;

      // PUT Block List URL
      const commitUrl = `${sasUrl}&comp=blocklist`;
      xhr.open('PUT', commitUrl);
      xhr.setRequestHeader('Content-Type', 'application/xml');
      
      xhr.send(blockListXml);
    });
  },

  /**
   * Complete direct blob upload and trigger backend processing
   */
  completeDirectUpload: async (
    uploadId: string,
    blobName: string,
    originalSize: number
  ): Promise<ApiResponse<any>> => {
    const payload = {
      uploadId,
      blobName,
      originalSize,
    };
    
    const response = await apiClient.post<ApiResponse<any>>('/upload/complete', payload);
    return handleApiResponse(response);
  },
};

/**
 * Files API for file management operations
 */
export const filesApi = {
  /**
   * Delete a file
   */
  deleteFile: async (fileId: string): Promise<ApiResponse> => {
    const response = await apiClient.delete<ApiResponse>(`/files/${fileId}`);
    return handleApiResponse(response);
  },

  /**
   * Sync file availability status (admin only)
   */
  syncAvailability: async (forceSync: boolean = false): Promise<ApiResponse> => {
    const response = await apiClient.post<ApiResponse>('/files/sync-availability', { forceSync });
    return handleApiResponse(response);
  },
};